import axios from 'axios';
import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from 'axios';
import type { ApiResponse } from '../../types/common/common.ts';
import { mockAdapter } from '../../mocks/transport.ts';
import { LOGIN_PATH, IS_INDEPENDENT_PORTAL, portalHref } from '../../app/portal.ts';
import { AUTH_MODE } from '../../services/auth/config.ts';
import { getMsalInstance, tokenRequest } from '../../app/msalConfig.ts';

const isEntra = AUTH_MODE === 'entra';
const configuredApiOrigin = import.meta.env.VITE_API_BASE_URL ??
  (typeof window === 'undefined' ? undefined : window.__QCDOC_PORTAL_CONFIG__?.apiBaseUrl);
const apiOrigin = isEntra && configuredApiOrigin && URL.canParse(configuredApiOrigin) ? new URL(configuredApiOrigin) : null;
const validApiOrigin = apiOrigin && !apiOrigin.username && !apiOrigin.password && apiOrigin.pathname === '/' &&
  !apiOrigin.search && !apiOrigin.hash && (apiOrigin.protocol === 'https:' || apiOrigin.hostname === 'localhost');

const apiClient: AxiosInstance = axios.create({
  adapter: isEntra ? undefined : mockAdapter,
  baseURL: apiOrigin?.origin,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30_000,
});

// ── Request Interceptor — Inject Bearer Token ──

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (!isEntra) config.adapter = mockAdapter;
    // A key missing from `Endpoints` evaluates to `undefined` rather than
    // throwing, and axios would then send the request to the current page URL.
    // The dev server answers that with index.html at HTTP 200, which carries no
    // `success` envelope, so callers read `data.items` as undefined and render an
    // empty grid with no error anywhere. Fail loudly instead.
    if (!config.url) {
      throw new Error(
        '[apiClient] Request blocked: endpoint URL is undefined. A key is likely missing from Endpoints (src/api/common/endpoints.ts).',
      );
    }

    if (isEntra) {
      if (!validApiOrigin) throw new Error('Entra mode requires a protected HTTPS API origin.');
      const instance = getMsalInstance();
      const account = instance.getActiveAccount();
      if (!account) throw new Error('No active Entra account. Sign in again.');
      const response = await instance.acquireTokenSilent({ ...tokenRequest, account });
      config.headers.Authorization = `Bearer ${response.accessToken}`;
      return config;
    }

    try {
      // Dynamically import to avoid circular dependency with auth module
      const { getAccessToken } = await import('../../services/auth/token.ts');
      const token = getAccessToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // Auth module not available or no token — proceed without auth header
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

// ── Response Interceptor — Unwrap ApiResponse<T> + Handle Errors ──

apiClient.interceptors.response.use(
  (response) => {
    // Skip unwrapping for blob/arraybuffer responses (file downloads)
    if (
      response.config.responseType === 'blob' ||
      response.config.responseType === 'arraybuffer'
    ) {
      return response;
    }

    // Unwrap ApiResponse<T> envelope: { success, data, message, errors }
    const body = response.data;
    if (body && typeof body === 'object' && 'success' in body) {
      if (!body.success) {
        // Server returned a structured error — reject with message
        const err = new Error(body.message || 'Request failed');
        (err as unknown as Record<string, unknown>).errors = body.errors;
        (err as unknown as Record<string, unknown>).response = response;
        return Promise.reject(err);
      }
      // Unwrap: callers get T directly via response.data
      response.data = body.data !== undefined ? body.data : body;
    }

    return response;
  },
  (error: AxiosError<ApiResponse>) => {
    const url = error.config?.url ?? '';
    const isAuthEndpoint = url.includes('/Auth/');

    if (error.response?.status === 401 && (isEntra || !isAuthEndpoint)) {
      // Token expired, revoked by sign-out, or invalid — drop the session and
      // send the user back to the matching sign-in page.
      console.warn('[apiClient] 401 Unauthorized:', url);
      if (isEntra) {
        void (async () => {
          const { getCommonStore } = await import('../../services/auth/bootstrap.ts');
          const { authActions } = await import('../../redux/slices/authSlice.ts');
          const { setAccessToken } = await import('../../services/auth/token.ts');
          getCommonStore().dispatch(authActions.clear());
          setAccessToken(null);
          const instance = getMsalInstance();
          await instance.logoutRedirect({ account: instance.getActiveAccount() ?? undefined });
        })();
        return Promise.reject(error);
      }
      void (async () => {
        const { useAuthStore } = await import('../../shared/stores/authStore.ts');
        const isVendor = useAuthStore.getState().user?.portal === 'QCDOC-VENDOR';
        useAuthStore.getState().clear();
        const currentPath = IS_INDEPENDENT_PORTAL ? window.location.hash.slice(1).split('?')[0] : window.location.pathname;
        if (!currentPath.endsWith('/login')) {
          window.location.href = portalHref(IS_INDEPENDENT_PORTAL ? LOGIN_PATH : isVendor ? '/vendor/login' : '/login');
        }
      })();
    }

    if (error.response?.status === 403) {
      window.location.href = portalHref('/access-denied');
    }

    return Promise.reject(error);
  },
);

export default apiClient;
